import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, tap } from 'rxjs/operators';
import {
  ADMIN_ROLE,
  DASHBOARD_ROLES,
  highestRole,
  ORGANISATION_ROLE_HEIRARCHY,
} from '@careapp-group/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of as observableOf } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { CAAction, Organisations, ResponseSuccessAuthLogoutAction } from '../actions';
import { OrganisationContextSwitch } from '../actions/meta.actions';
import { auth } from '../auth/auth.selectors';
import * as selectors from '../reducers';
import { CaDynamicThemeService } from '../../ca-design/services/dynamic-theme.service';
import {
  OrganisationBrandingDto,
  OrganisationConfigDto,
  OrganisationDto,
  OrganisationPreferenceDto,
  OrganisationsService,
} from '@careapp-group/ng-sdk';

@Injectable()
export class OrganisationEffects {
  findUserOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_OWN),
      withLatestFrom(this.store.select(auth.isAdmin)),
      switchMap(([action, isAdmin]) =>
        this.organisationsService.listOrganisations().pipe(
          map((payload: OrganisationDto[]) => {
            if (!isAdmin) {
              payload = payload.filter((value) =>
                DASHBOARD_ROLES.includes(
                  highestRole(value.roles, [ADMIN_ROLE, ...ORGANISATION_ROLE_HEIRARCHY])
                )
              );
            }
            return Organisations.ResponseSuccessOwn(payload);
          }),
          catchError((err) => {
            return observableOf(Organisations.ResponseFailOwn(err));
          })
        )
      )
    )
  );

  saveOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_SAVE),
      switchMap((action) =>
        this.organisationsService
          .editOrganisation({ id: action.payload.id, updateOrganisationDto: action.payload.data })
          .pipe(
            map((payload: OrganisationDto) => {
              return Organisations.ResponseSuccessSaveAction(payload);
            }),
            catchError((err) => {
              return observableOf(Organisations.ResponseFailSaveAction(err));
            })
          )
      )
    )
  );

  getOrganisations = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_ALL),
      switchMap((action) => {
        return this.organisationsService.listOrganisations().pipe(
          map((payload) => {
            const sorted = payload.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            });
            return Organisations.ResponseSuccessAllAction(sorted);
          }),
          catchError((err) => {
            return observableOf(Organisations.ResponseFailAllAction(err));
          })
        );
      })
    )
  );

  organisationDetail = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_BY_ID),
      switchMap((action: CAAction) => {
        return this.organisationsService.getOrganisation({ id: action.payload.id }).pipe(
          map((payload: OrganisationDto) => {
            return Organisations.ResponseSuccessByIdAction(payload);
          }),
          catchError((err) => {
            return observableOf(Organisations.ResponseFailByIdAction(err));
          })
        );
      })
    )
  );

  createOrganisation = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_CREATE),
      switchMap((action: CAAction) => {
        return this.organisationsService
          .createOrganisation({ createOrganisationDto: action.payload })
          .pipe(
            map((response) => {
              return Organisations.ResponseSuccessCreateAction(response);
            }),
            catchError((err) => {
              return observableOf(Organisations.ResponseFailCreateAction(err));
            })
          );
      })
    )
  );

  refreshOrgsAfterCreate = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.RESPONSE_SUCCESS_CREATE),
      map((action) => Organisations.RequestOwn())
    )
  );

  getPreferences = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_PREFERENCES),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      filter(([action, id]) => !!id),
      switchMap(([action, id]) => {
        return this.organisationsService.getOrganisationPreferences({ id }).pipe(
          map((res: OrganisationPreferenceDto) =>
            Organisations.ResponseSuccessPreferences({
              ...res,
              organisationId: id,
            })
          ),
          catchError((err) => observableOf(Organisations.ResponseFailPreferences(err)))
        );
      })
    )
  );

  updatePreferences = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.UPDATE_PREFERENCES),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      mergeMap(([action, id]) => {
        return this.organisationsService
          .updateOrganisationPreferences({ id: id, organisationPreferenceDto: action.payload })
          .pipe(
            map((res: OrganisationPreferenceDto) =>
              Organisations.SuccessUpdatePreferences({ ...res, organisationId: id })
            ),
            catchError((err) => observableOf(Organisations.FailUpdatePreferences(err)))
          );
      })
    )
  );

  getOrgConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_ORG_CONFIG),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      filter(([action, id]) => !!id),
      switchMap(([action, id]) => {
        const organisationId = action.payload?.organisationId ?? id;
        return this.organisationsService.getOrganisationConfig({ id: organisationId }).pipe(
          filter((data) => !!data),
          map((res: OrganisationConfigDto) => {
            return Organisations.ResponseSuccessOrgConfig({
              ...res,
              organisationId: id,
            });
          }),
          catchError((err) => observableOf(Organisations.ResponseFailOrgConfig(err)))
        );
      })
    )
  );

  updateOrgConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.UPDATE_ORG_CONFIG),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      mergeMap(([action, id]) => {
        return this.organisationsService
          .updateOrganisationConfig({ id, organisationConfigDto: action.payload.config })
          .pipe(
            map((res: OrganisationConfigDto) =>
              Organisations.SuccessUpdateOrgConfig({ ...res, organisationId: id })
            ),
            catchError((err) => observableOf(Organisations.FailUpdateOrgConfig(err)))
          );
      })
    )
  );

  getBranding = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REQUEST_BRANDING),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      filter(([action, id]) => !!id),
      switchMap(([action, id]) => {
        return this.organisationsService.getOrganisationBranding({ id }).pipe(
          map((res: OrganisationBrandingDto) =>
            Organisations.ResponseSuccessBranding({ ...res, organisationId: id })
          ),
          catchError((err) => observableOf(Organisations.ResponseFailBranding(err)))
        );
      })
    )
  );

  updateBrandingColors = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.UPDATE_BRANDING_COLORS),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, id]) => {
        return this.organisationsService
          .updateOrganisationBrandingColours({ id, colorSettingsDto: action.payload })
          .pipe(
            map((res: OrganisationBrandingDto) =>
              Organisations.SuccessUpdateBrandingColors({
                ...res,
                organisationId: id,
              })
            ),
            catchError((err) => observableOf(Organisations.FailUpdateBrandingColors(err)))
          );
      })
    )
  );

  resetBrandingColors = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.RESET_BRANDING_COLORS),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, id]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Reset Colors?',
              body: 'Are you sure you want to reset your branding colors to the app defaults?',
              confirm: 'Reset',
              cancel: 'Cancel',
              confirmColor: 'warn',
            },
          })
          .afterClosed()
          .pipe(map((result) => [action, id, result]))
      ),
      switchMap(([action, id, result]) =>
        result
          ? this.organisationsService
              .updateOrganisationBrandingColours({
                id,
                colorSettingsDto: {
                  primary: null,
                  secondary: null,
                  fabForeground: null,
                  fabBackground: null,
                  fabShadow: null,
                },
              })
              .pipe(
                map((res: OrganisationBrandingDto) =>
                  Organisations.SuccessUpdateBrandingColors({
                    ...res,
                    organisationId: id,
                  })
                ),
                catchError((err) => observableOf(Organisations.FailUpdateBrandingColors(err)))
              )
          : observableOf({ type: 'CANCELLED' })
      )
    )
  );

  removeBrandingImage = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.REMOVE_BRANDING_IMAGE),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, id]) =>
        this.dialog
          .open(ConfirmationDialogComponent, {
            data: {
              title: 'Reset Image?',
              body: 'Are you sure you want to reset this image to the application default? This can not be undone',
              confirm: 'Reset',
              cancel: 'Cancel',
              confirmColor: 'warn',
            },
          })
          .afterClosed()
          .pipe(map((result) => [action, id, result]))
      ),
      switchMap(([action, id, result]) =>
        result
          ? this.organisationsService
              .updateOrganisationBrandingMedia({
                id,
                fileRecordDto: { ...action.payload, purpose: null },
              })
              .pipe(
                map((res: OrganisationBrandingDto) =>
                  Organisations.SuccessUpdateBrandingImage({
                    ...res,
                    organisationId: id,
                  })
                ),
                catchError((err) => observableOf(Organisations.FailUpdateBrandingImage(err)))
              )
          : observableOf({ type: 'CANCELLED' })
      )
    )
  );

  updateBrandingMedia = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.UPDATE_BRANDING_IMAGE),
      withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
      switchMap(([action, id]) => {
        return this.organisationsService
          .updateOrganisationBrandingMedia({ id, fileRecordDto: action.payload })
          .pipe(
            map((res: OrganisationBrandingDto) =>
              Organisations.SuccessUpdateBrandingImage({
                ...res,
                organisationId: id,
              })
            ),
            catchError((err) => observableOf(Organisations.FailUpdateBrandingImage(err)))
          );
      })
    )
  );

  contextSwitch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.SELECT_BY_ID),
      map(() => OrganisationContextSwitch())
    )
  );

  applyOrgColors = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          Organisations.RESPONSE_SUCCESS_BRANDING,
          Organisations.SUCCESS_UPDATE_BRANDING_COLORS,
          Organisations.SELECT_BY_ID,
          ResponseSuccessAuthLogoutAction
        ),
        withLatestFrom(this.store.select(selectors.org.currentOrgBranding)),
        tap(([action, currentOrgBranding]: [any, OrganisationBrandingDto]) => {
          const branding = currentOrgBranding ?? <OrganisationBrandingDto>action.payload;
          if (branding?.colors?.primary) {
            this.themeService.applyThemeFromOrganisationBranding(
              branding.colors.primary,
              branding.colors.fabForeground,
              branding.colors.fabBackground,
              branding.colors.fabShadow
            );
          } else {
            this.themeService.applyCareAppTheme();
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private organisationsService: OrganisationsService,
    private dialog: MatDialog,
    private themeService: CaDynamicThemeService
  ) {}
}
